import React from 'react';
import {
  FormProvider,
} from 'react-hook-form';
import clsx from 'clsx';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Controller from 'core/components/FormController';
import Button from 'pages/Dashboard/components/Button';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Spinner from 'pages/Dashboard/components/Spinner';
import FeeSchedule from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/FeeSchedule';
import ProviderLookup from 'views/EMR/components/ProviderLookup';
import PopulateUrl from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCLearnMoreLink';
import {
  PCC_LEARN_MORE_URL,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCActivationSettingsPanel';
import {
  usePCCSettingsForm,
} from 'views/EMR/Settings/PCC/usePCCSettingsForm';

type PCCSettingsFormProps = {
  className?: string;
  onClose?: () => void;
};

export function getPCCSettingsCopy(isActivated: boolean) {
  return isActivated ? {
    title: 'PCC Settings',
    idleLabel: 'Save Changes',
    pendingLabel: 'Saving...',
    successMessage: 'PCC settings updated successfully',
  } : {
    title: 'Activate PCC',
    idleLabel: 'Activate PCC',
    pendingLabel: 'Activating...',
    successMessage: 'PCC activated successfully',
  };
}

export default function PCCSettingsForm({
  className,
  onClose,
}: PCCSettingsFormProps) {
  const {
    formProps,
    control,
    handleSubmit,
    isValid,
    isLoadingDefaults,
    isSaving,
    isFormDisabled,
    isActivated,
    showCcmFeeSchedule,
    isValidFeeSchedule,
    onSubmit,
  } = usePCCSettingsForm({
    getSuccessMessage: (activated) => getPCCSettingsCopy(activated).successMessage,
    onSuccess: onClose,
  });
  const panelCopy = getPCCSettingsCopy(isActivated);

  return (
    <FormProvider {...formProps}>
      <form
        className={clsx(
          'flex h-full min-h-0 flex-col overflow-hidden bg-white',
          className,
        )}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {isLoadingDefaults ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size={24} />
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <section className="flex flex-col gap-4">
                <h4 className="text-base font-semibold text-gray-900">TEMPO</h4>
                <div className="flex flex-wrap gap-4">
                  <div className="min-w-0 flex-[1_1_25rem]">
                    <Controller
                      name="tempoProvider"
                      control={control}
                      rules={{ required: 'Select TEMPO provider' }}
                      render={({ field, fieldState: { invalid } }) => (
                        <Wrapper
                          id="tempo-provider"
                          label="Provider"
                          required
                          error={invalid}
                          showErrorText={false}
                          className="text-xs text-gray-500"
                        >
                          <ProviderLookup
                            {...field}
                            id="tempo-provider"
                            disabled={isFormDisabled}
                          />
                        </Wrapper>
                      )}
                    />
                  </div>
                  <div className="min-w-0 flex-[1_1_25rem]">
                    <Controller
                      name="suicideAlertAssignee"
                      control={control}
                      rules={{ required: 'Select suicide alert assignee' }}
                      render={({ field, fieldState: { invalid } }) => (
                        <Wrapper
                          id="suicide-alert-assignee"
                          label="Suicide Alert Assignee"
                          required
                          error={invalid}
                          showErrorText={false}
                          className="text-xs text-gray-500"
                        >
                          <ProviderLookup
                            {...field}
                            id="tempo-suicide-alert-assignee"
                            disabled={isFormDisabled}
                            placeholder="Select Assignee"
                            onlyDoctors={false}
                          />
                        </Wrapper>
                      )}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Fee Schedule
                  </h4>
                  <Controller
                    name="tempoFeeSchedule"
                    rules={{
                      validate: (feeSchedule) => (
                        isValidFeeSchedule(feeSchedule) || 'Provide a valid fee schedule for TEMPO'
                      ),
                    }}
                    render={({ field, fieldState: { invalid } }) => (
                      <FeeSchedule
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isFormDisabled}
                        error={invalid}
                      />
                    )}
                  />
                </div>
              </section>
              {showCcmFeeSchedule ? (
                <section className="flex flex-col gap-4">
                  <h4 className="text-base font-semibold text-gray-900">CCM</h4>
                  <div className="flex flex-col gap-2">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Fee Schedule
                    </h4>
                    <Controller
                      name="ccmFeeSchedule"
                      rules={{
                        validate: (feeSchedule) => (
                          isValidFeeSchedule(feeSchedule) || 'Provide a valid fee schedule for CCM'
                        ),
                      }}
                      render={({ field, fieldState: { invalid } }) => (
                        <FeeSchedule
                          value={field.value}
                          onChange={field.onChange}
                          disabled={isFormDisabled}
                          error={invalid}
                        />
                      )}
                    />
                  </div>
                </section>
              ) : null}
              <section className="flex flex-col gap-4">
                <h4 className="text-base font-semibold text-gray-900">RTM</h4>
                <div className="flex flex-col gap-2">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Fee Schedule
                  </h4>
                  <Controller
                    name="rtmFeeSchedule"
                    rules={{
                      validate: (feeSchedule) => (
                        isValidFeeSchedule(feeSchedule) || 'Provide a valid fee schedule for RTM'
                      ),
                    }}
                    render={({ field, fieldState: { invalid } }) => (
                      <FeeSchedule
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isFormDisabled}
                        error={invalid}
                      />
                    )}
                  />
                </div>
              </section>
            </div>
          )}
        </div>
        <div className={clsx('flex shrink-0 justify-start border-t border-gray-200 px-4 py-3', {
          'justify-between items-center': !isActivated,
          'justify-start': isActivated,
        })}
        >
          {!isActivated ? (
            <div className="text-[11px] text-gray-700 font-medium">
              By activating, you accept the
              {' '}
              <PopulateUrl url={PCC_LEARN_MORE_URL} label="PCC Terms" />
              {' '}
              on behalf of your practice.
            </div>
          ) : null}
          <DisabledFormTooltip>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isValid || isFormDisabled}
              className={isSaving ? 'is-pending' : ''}
            >
              {isSaving ? panelCopy.pendingLabel : panelCopy.idleLabel}
            </Button>
          </DisabledFormTooltip>
        </div>
      </form>
    </FormProvider>
  );
}
